import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "@bitsentry-ce/i18n";

export function useOfflineSync() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    let syncInProgress = false;

    const handleOnline = async () => {
      if (syncInProgress) return;

      syncInProgress = true;

      // Show sync notification
      const toastId = toast.loading(t("common.useOfflineSyncTs.syncingData"), {
        description: t(
          "common.useOfflineSyncTs.refreshingYourDataFromTheServer",
        ),
      });

      try {
        // Invalidation already refetches active queries. A second refetch would
        // repeat every request after the first pass has completed.
        await queryClient.invalidateQueries(
          { refetchType: "active" },
          { throwOnError: true },
        );

        toast.success(t("common.useOfflineSyncTs.dataSyncedSuccessfully"), {
          id: toastId,
          description: t("common.useOfflineSyncTs.yourDataIsNowUpToDate"),
        });
      } catch {
        toast.error(t("errors.offlineSync.failedToSync"), {
          id: toastId,
          description: t(
            "common.useOfflineSyncTs.someDataMayNotBeUpToDatePleaseRefreshThePage",
          ),
        });
      } finally {
        syncInProgress = false;
      }
    };

    const handleOnlineEvent = () => {
      void handleOnline();
    };

    // Listen for when we come back online
    window.addEventListener("online", handleOnlineEvent);

    // Sync on mount if online (silently, without toast)
    if (navigator.onLine) {
      const cache = queryClient.getQueryCache();
      const hasStaleData = cache
        .getAll()
        .some((query) => query.state.isInvalidated);

      if (hasStaleData) {
        queryClient.refetchQueries({ type: "active" }).catch(() => {});
      }
    }

    return () => {
      window.removeEventListener("online", handleOnlineEvent);
    };
  }, [queryClient, t]);
}
